import { useState } from "react";
import { ListPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { CrudPanel } from "@/components/library/CrudPanel";
import { PresetStepsField } from "@/components/workorders/PresetStepsField";
import { AddStepToPresetsDialog } from "@/components/workorders/AddStepToPresetsDialog";

const APPLIES = [{ value: "any", label: "Any" }, { value: "install", label: "Installation" }, { value: "service", label: "Service call" }, { value: "takedown", label: "Takedown" }];
const norm = (s: string) => (s || "").trim().toLowerCase();

export default function SettingsChecklists() {
  const { user } = useAuth() as any;
  const admin = user?.role === "admin";
  const [presets, setPresets] = useState<any[]>([]);
  const [ver, setVer] = useState(0);
  const [addStep, setAddStep] = useState<any | null>(null);
  const usedIn = (text: string) => presets.filter((p) => (p.items || []).some((t: string) => norm(t) === norm(text)));
  return (
    <AppShell title="Settings · Checklists">
      <p className="mb-4 text-sm text-gray-500">Saved steps are single reusable checklist items — add one to as many checklists as you like without retyping. Checklist presets are complete lists you can drop onto a work order in one click. Both appear in the “Add from…” menus on every work order.</p>
      <div className="space-y-8">
        <CrudPanel path="wo-checklist-items" title="Saved checklist steps" testid="wo-items" canEdit emptyText="Save a step from any work order checklist (bookmark icon), or add one here."
          fields={[
            { key: "text", label: "Step" },
            { key: "type", label: "Applies to", type: "select", options: APPLIES },
            { key: "_used", label: "Used in", hideInTable: false, render: (r) => { const u = usedIn(r.text); return <span className="text-xs text-gray-500" data-testid={`wo-items-used-${r.id}`}>{u.length ? u.map((p) => p.name).join(", ") : "—"}</span>; }, showIf: () => false },
          ]}
          rowActions={(r) => admin && <button data-testid={`wo-items-add-to-${r.id}`} title="Add this step to one or more checklists" onClick={() => setAddStep(r)} className="mr-1 inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:bg-gray-50"><ListPlus size={12} />Add to checklists</button>} />
        <CrudPanel key={ver} path="wo-checklist-presets" title="Checklist presets (full lists)" testid="wo-presets" canEdit={admin} wide newRow={{ items: [], type: "install" }} emptyText="Add reusable installer checklists (e.g. New Install, Service call)." onRows={setPresets}
          fields={[
            { key: "name", label: "Preset name" },
            { key: "type", label: "Applies to", type: "select", options: APPLIES.slice(1).concat(APPLIES.slice(0, 1)) },
            { key: "items", label: "Checklist steps", type: "custom", edit: (e, set) => <PresetStepsField key={e.id || "new"} value={Array.isArray(e.items) ? e.items : []} type={e.type || "any"} onChange={(items) => set({ items })} /> },
          ]} />
      </div>
      {addStep && <AddStepToPresetsDialog step={addStep} presets={presets} onClose={() => setAddStep(null)} onDone={() => setVer((v) => v + 1)} />}
    </AppShell>
  );
}
